import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import {
  fetchKatlasDiagnostics,
  requestLiveSign,
  requestVerify,
} from "../lib/katlas";

// LiveKatlasReceipt
// Three explicit UI states (per Edward's brief, §4):
//   1. "Unsigned — pending live KATLAS key"  → no bearer token visible to backend
//   2. "Live KATLAS signing failed"          → token present, backend call to KATLAS failed
//                                              (shows safe diagnostic code, no secret material)
//   3. "Live, signed"                        → receipt hash + node timestamp + signer ref
//                                              + verification status (verified | rejected | unknown)
//
// The component fetches /api/katlas/diagnostics on mount to decide whether the
// "Sign live with KATLAS" action is even offered. If the backend reports
// mode != "live" or bearer_token_present == false, the action is suppressed
// and state 1 is shown.

const stateMeta = {
  loading_diagnostics: {
    label: "Checking KATLAS configuration…",
    tone: "neutral",
  },
  missing_key: {
    label: "Unsigned — pending live KATLAS key",
    tone: "amber",
  },
  ready_to_sign: {
    label: "Ready to sign live with KATLAS",
    tone: "green",
  },
  signing: {
    label: "Submitting to KATLAS…",
    tone: "neutral",
  },
  signed: {
    label: "Live KATLAS signed receipt",
    tone: "green",
  },
  bearer_token_rejected: {
    label: "Bearer token rejected by KATLAS",
    tone: "red",
  },
  node_unreachable: {
    label: "KATLAS node unreachable",
    tone: "red",
  },
  signing_failed: {
    label: "Live KATLAS signing failed",
    tone: "red",
  },
  verifying: {
    label: "Verifying with KATLAS…",
    tone: "neutral",
  },
  verify_failed: {
    label: "Live KATLAS verification failed",
    tone: "red",
  },
};

const toneClasses = {
  neutral: "border-border bg-white text-foreground",
  amber: "border-amber-700/50 bg-white text-amber-800",
  green: "border-emerald-700/50 bg-white text-emerald-800",
  red: "border-destructive/50 bg-white text-destructive",
};

const StatusPill = ({ state }) => {
  const meta = stateMeta[state] || { label: state, tone: "neutral" };
  return (
    <span
      data-testid={`live-katlas-state-${state}`}
      className={`inline-flex items-center gap-2 border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${toneClasses[meta.tone]}`}
    >
      <span
        className={`h-1.5 w-1.5 ${
          meta.tone === "green"
            ? "bg-emerald-600"
            : meta.tone === "red"
              ? "bg-destructive"
              : meta.tone === "amber"
                ? "bg-amber-600"
                : "bg-muted-foreground"
        }`}
      />
      {meta.label}
    </span>
  );
};

const Field = ({ label, value, mono = true }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </p>
    <p
      className={`mt-1 break-all text-sm text-foreground ${mono ? "font-mono" : ""}`}
    >
      {value === null || value === undefined || value === "" ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        String(value)
      )}
    </p>
  </div>
);

export const LiveKatlasReceipt = ({
  savedDraft,
  output,
  wallet,
  onLiveResultChange,
}) => {
  const [diagnostics, setDiagnostics] = useState(null);
  const [state, setState] = useState("loading_diagnostics");
  const [result, setResult] = useState(null); // signed payload
  const [error, setError] = useState(null);

  // Notify parent when a live result lands so the illustrative panel can
  // be demoted to "reference" (per §5 of Edward's brief).
  useEffect(() => {
    if (typeof onLiveResultChange === "function") {
      onLiveResultChange(state === "signed" ? result : null);
    }
  }, [state, result, onLiveResultChange]);

  // Load diagnostics once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchKatlasDiagnostics();
        if (cancelled) return;
        setDiagnostics(d);
        const liveReady =
          d.katlas_mode === "live" &&
          d.katlas_bearer_token_present &&
          d.katlas_node_url_present;
        if (d.katlas_mode === "live_error") {
          // Map the cached last_error_type to a granular UI state so the
          // pill on mount reflects what kind of failure was last recorded.
          const httpCode = d.status_endpoint_http_code;
          let mappedState;
          if (httpCode === 401 || httpCode === 403) {
            mappedState = "bearer_token_rejected";
          } else if (
            d.last_error_type === "NetworkError" ||
            d.last_error_type === "TimeoutError"
          ) {
            mappedState = "node_unreachable";
          } else {
            mappedState = "signing_failed";
          }
          setState(mappedState);
          setError({
            error_type: d.last_error_type || "Unknown",
            diagnostic_code: `KATLAS_DIAG_${(d.last_error_type || "UNKNOWN").toUpperCase()}_${httpCode ?? "NO_HTTP"}`,
            http_code: httpCode,
          });
        } else {
          setState(liveReady ? "ready_to_sign" : "missing_key");
        }
      } catch (e) {
        if (cancelled) return;
        setState("missing_key");
        setError({
          error_type: "DiagnosticsUnavailable",
          diagnostic_code: "KATLAS_DIAGNOSTICS_UNREACHABLE",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSign = async () => {
    setState("signing");
    setError(null);
    try {
      const res = await requestLiveSign({
        draftId: savedDraft?.id || null,
        headline: output.headline,
        selection: {
          user_type: output.selection.user_type,
          sector: output.selection.sector,
          problem: output.selection.problem,
          scope: output.selection.scope,
        },
        walletFingerprint: wallet?.fingerprint || null,
        walletHandle: wallet?.handle || null,
      });
      if (res.ok && res.state === "signed") {
        setResult(res);
        setState("signed");
      } else if (res.state === "missing_key") {
        setError({
          error_type: res.error_type,
          diagnostic_code: res.diagnostic_code,
        });
        setState("missing_key");
      } else if (
        res.state === "bearer_token_rejected" ||
        res.state === "node_unreachable" ||
        res.state === "signing_failed"
      ) {
        setError({
          error_type: res.error_type,
          diagnostic_code: res.diagnostic_code,
          http_code: res.http_code,
        });
        setState(res.state);
      } else {
        setError({
          error_type: res.error_type || "Unknown",
          diagnostic_code: res.diagnostic_code,
          http_code: res.http_code,
        });
        setState("signing_failed");
      }
    } catch (e) {
      setError({
        error_type: "NetworkError",
        diagnostic_code: "KATLAS_SIGN_NETWORKERROR_NO_HTTP",
      });
      setState("node_unreachable");
    }
  };

  const handleVerify = async () => {
    if (!result?.envelope_id) return;
    setState("verifying");
    try {
      const v = await requestVerify(result.envelope_id);
      if (v.ok) {
        setResult((prev) => ({
          ...prev,
          verification_status: v.verification_status,
        }));
        setState("signed");
      } else {
        setError({
          error_type: v.error_type,
          diagnostic_code: v.diagnostic_code,
          http_code: v.http_code,
        });
        setState("verify_failed");
      }
    } catch (e) {
      setError({
        error_type: "NetworkError",
        diagnostic_code: "KATLAS_VERIFY_NETWORKERROR_NO_HTTP",
      });
      setState("verify_failed");
    }
  };

  return (
    <article
      data-testid="live-katlas-receipt"
      className="border border-foreground bg-white"
    >
      <header className="border-b border-border bg-[#FAFAFA] p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <StatusPill state={state} />
          {diagnostics && (
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              katlas_mode={diagnostics.katlas_mode} · token={diagnostics.token_source_used}
            </span>
          )}
        </div>
        <h4 className="mt-4 font-serif text-2xl font-light leading-tight tracking-tight text-foreground">
          {state === "signed"
            ? "Live KATLAS signed receipt"
            : state === "missing_key"
              ? "No live KATLAS receipt — pending live key"
              : state === "bearer_token_rejected"
                ? "KATLAS rejected the bearer token"
                : state === "node_unreachable"
                  ? "KATLAS node could not be reached"
                  : state === "signing_failed"
                    ? "Live KATLAS signing failed"
                    : state === "verify_failed"
                      ? "Live KATLAS verification failed"
                      : "Sign this draft live with KATLAS"}
        </h4>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          {state === "missing_key"
            ? "Production KATLAS bearer token is not present in this environment. No live signing is attempted. This is not a failure of the signing call — it is the absence of a live key."
            : state === "bearer_token_rejected"
              ? "The bearer token reached the KATLAS node but was rejected (HTTP 401/403). The token is present in this environment, but it is not valid for this node. Diagnostic code below; no secret material is exposed."
              : state === "node_unreachable"
                ? "The bearer token is present but the KATLAS node could not be reached (network error or timeout). This is not a missing key and not a rejected token — it is an unreachable node. Diagnostic code below."
                : state === "signing_failed"
                  ? "The bearer token is accepted by the network, the node responded, but the signing call did not produce a 2xx. Diagnostic code below; no secret material is exposed."
                  : state === "signed"
                    ? "Signed by the production KATLAS node. The full signed_receipt envelope is held server-side; the values shown below are the safe public summary."
                    : state === "ready_to_sign"
                      ? "KATLAS is configured. Submit the canonical payload to the production node for a live signed receipt."
                      : state === "signing"
                        ? "Posting to /v1/receipts/sign…"
                        : state === "verifying"
                          ? "Posting to /v1/receipts/verify…"
                          : state === "verify_failed"
                            ? "The signed receipt is held server-side, but verification against KATLAS did not succeed. Diagnostic code below."
                            : "Reading current KATLAS configuration from the backend…"}
        </p>
      </header>

      <div className="grid gap-5 p-6">
        {state === "ready_to_sign" && (
          <div>
            <Button
              type="button"
              data-testid="live-katlas-sign-cta"
              onClick={handleSign}
              className="rounded-none font-mono text-xs uppercase tracking-[0.18em]"
            >
              Sign live with KATLAS →
            </Button>
          </div>
        )}

        {state === "signed" && result && (
          <>
            <Field
              label="Receipt hash"
              value={result.receipt_hash}
            />
            <Field
              label="Node timestamp"
              value={result.node_timestamp}
              mono
            />
            <Field
              label="Signer / attestation reference"
              value={result.signer_ref}
            />
            <Field
              label="Envelope id (server-side)"
              value={result.envelope_id}
            />
            <Field
              label="Verification status"
              value={result.verification_status || "not yet verified"}
              mono={false}
            />
            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                data-testid="live-katlas-verify-cta"
                variant="outline"
                onClick={handleVerify}
                className="rounded-none border-foreground/40 bg-white font-mono text-xs uppercase tracking-[0.18em] hover:border-foreground"
              >
                {result.verification_status
                  ? "Re-verify with KATLAS"
                  : "Verify with KATLAS"}
              </Button>
            </div>
          </>
        )}

        {(state === "bearer_token_rejected" ||
          state === "node_unreachable" ||
          state === "signing_failed" ||
          state === "verify_failed") && error && (
          <>
            <Field label="Error type" value={error.error_type} />
            <Field
              label="HTTP code from KATLAS"
              value={
                error.http_code === undefined || error.http_code === null
                  ? "—"
                  : String(error.http_code)
              }
            />
            <Field
              label="Safe diagnostic code"
              value={error.diagnostic_code}
            />
            <p className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.16em] text-muted-foreground">
              No bearer token, no headers, no response body, and no
              partial signed-receipt material is exposed. Operator should
              query <span className="text-foreground">/api/katlas/diagnostics</span> for the
              full safe snapshot.
            </p>
            {state !== "verify_failed" && (
              <div>
                <Button
                  type="button"
                  data-testid="live-katlas-retry-cta"
                  onClick={handleSign}
                  variant="outline"
                  className="rounded-none border-foreground/40 bg-white font-mono text-xs uppercase tracking-[0.18em] hover:border-foreground"
                >
                  Retry signing
                </Button>
              </div>
            )}
          </>
        )}

        {state === "missing_key" && (
          <p className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.16em] text-muted-foreground">
            Live signing is unavailable in this environment because the
            production KATLAS credential has not yet been provisioned to
            the backend runtime. The reference shapes below are
            illustrative material — they are not a live KATLAS receipt.
          </p>
        )}
      </div>
    </article>
  );
};
